// Snik Service - Firestore-integrasjon for «Hvor er sniken?»
//
// Følger samme konvensjoner som firebaseService.js: gjenbruker samme
// db/appId/auth-instans (ingen ny Firebase-app initialiseres her), og samme
// rense-/valideringsmønster for defensiv normalisering av data fra Firestore.
//
// Egen samling, atskilt fra .../catches:
//   artifacts/{appId}/users/{uid}/sniker/{snikId}
import firebaseService from './firebaseService';
import offlineQueue from './offlineQueue';
import {
  nesteLopenummer,
  lagNySnik,
  trekkOpp,
  leggTilArt as leggTilArtUtil,
  endreAntall as endreAntallUtil,
  fjernArt as fjernArtUtil
} from '../utils/snikUtils';
import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

class SnikService {
  constructor() {
    // Sist kjente, abonnerte sniker-liste (satt av subscribeToSniker).
    // Brukes til å beregne neste ledige Snik_Løpenummer i settSnik, i tråd
    // med design.md ("Auto-nummerering"): løpenummer beregnes fra den
    // allerede abonnerte listen, ikke via et ekstra Firestore-kall.
    this._lastKnownSniker = [];
    // Hindrer parallelle kjøringer av syncOfflineSniker (race condition).
    this._syncingSniker = false;
  }

  // Bygger referansen til brukerens sniker-samling: .../users/{uid}/sniker.
  // Brukt av både subscribeToSniker og settSnik for å unngå duplisert oppsett.
  snikerCollectionRef() {
    return collection(
      firebaseService.db,
      'artifacts',
      firebaseService.appId,
      'users',
      firebaseService.auth.currentUser.uid,
      'sniker'
    );
  }

  // Abonner på brukerens sniker, sortert på tidspunktSatt (nyeste først).
  // Speiler firebaseService.subscribeToCatches, men mot .../sniker.
  // Hvert dokument normaliseres defensivt slik at et enkelt korrupt/ufullstendig
  // dokument aldri kaster feil eller stopper resten av abonnementet.
  //
  // Motstandsdyktig mot avbrutte abonnementer: onSnapshot sitt feil-callback
  // kalles kun ÉN gang og lytteren dør permanent etter det (f.eks. ved en
  // midlertidig "permission-denied"/"unavailable"-feil rett etter innlogging,
  // eller ved en tilkoblingsglipp). Uten gjenoppkobling ville brukeren måtte
  // laste siden på nytt for å se nye/oppdaterte sniker — uakseptabelt i en
  // PWA som skal fungere live. Vi kobler derfor automatisk opp på nytt (med
  // økende ventetid) ved feil, og umiddelbart når nettleseren melder 'online'.
  subscribeToSniker(callback) {
    if (!firebaseService.auth.currentUser) {
      callback([]);
      return () => {};
    }

    let stoppet = false;
    let aktivUnsubscribe = null;
    let reconnectTimer = null;
    let reconnectForsok = 0;

    const koble = () => {
      if (stoppet || !firebaseService.auth.currentUser) return;

      const q = query(
        this.snikerCollectionRef(),
        orderBy('tidspunktSatt', 'desc')
      );

      aktivUnsubscribe = onSnapshot(q, (snapshot) => {
        reconnectForsok = 0; // vellykket snapshot: nullstill backoff
        const sniker = snapshot.docs
          .map((doc) => this.normalizeSnikDoc(doc))
          .filter(Boolean); // hopp over dokumenter som ikke kunne normaliseres i det hele tatt
        this._lastKnownSniker = sniker;
        callback(sniker);
      }, (error) => {
        console.error('Feil ved henting av sniker:', error);
        callback([]);
        planleggReconnect();
      });
    };

    const planleggReconnect = () => {
      if (stoppet) return;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      // Økende ventetid (1s, 2s, 4s ... maks 30s) for å unngå å hamre på
      // Firestore/auth ved en varig feil.
      const ventetid = Math.min(30000, 1000 * 2 ** reconnectForsok);
      reconnectForsok += 1;
      reconnectTimer = setTimeout(() => {
        if (!stoppet) koble();
      }, ventetid);
    };

    const paaOnline = () => {
      // Koble umiddelbart opp på nytt når tilkoblingen er tilbake, i stedet
      // for å vente på neste planlagte reconnect-forsøk.
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectForsok = 0;
      koble();
    };
    window.addEventListener('online', paaOnline);

    koble();

    return () => {
      stoppet = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      window.removeEventListener('online', paaOnline);
      if (aktivUnsubscribe) aktivUnsubscribe();
    };
  }

  // Opprett en ny Snik ved den bekreftede posisjonen.
  //
  // - Beregner tidspunktSatt som Date.now() ved opprettelse (Requirement 3.5).
  // - Beregner Snik_Løpenummer via nesteLopenummer, basert på den allerede
  //   abonnerte sniker-listen (Requirement 3.7).
  // - Lagrer posisjonen uendret, med full GPS-presisjon, uavhengig av
  //   brukerens Personverninnstilling — den gjelder kun ordinære Fangster
  //   (Requirement 3.8).
  // - Skriver til Firestore under .../sniker. Ved `unavailable`/nettverksfeil
  //   (eller hvis enheten allerede er offline) legges Snik i stedet i
  //   offline-køen ('pending-sniker'), for synkronisering når tilkobling er
  //   gjenopprettet (Requirement 3.10).
  async settSnik(posisjon) {
    if (!firebaseService.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }

    const tidspunktSatt = Date.now();
    const renPosisjon = {
      lat: parseFloat(posisjon.lat),
      lng: parseFloat(posisjon.lng)
    };
    const lopenummer = nesteLopenummer(this._lastKnownSniker);
    const nySnik = lagNySnik(renPosisjon, tidspunktSatt, lopenummer);
    const dataMedEier = {
      ...nySnik,
      userId: firebaseService.auth.currentUser.uid
    };

    try {
      if (!navigator.onLine) {
        throw { code: 'unavailable' };
      }

      const docRef = doc(this.snikerCollectionRef());
      await setDoc(docRef, {
        ...dataMedEier,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      if (error.code === 'unavailable' || error.message?.includes('network') || !navigator.onLine) {
        try {
          await offlineQueue.addTo('pending-sniker', dataMedEier);
          return 'offline-queued';
        } catch (queueError) {
          throw new Error('Kunne ikke lagre snik offline');
        }
      }
      console.error('Kunne ikke opprette snik:', error);
      throw new Error(`Kunne ikke opprette snik: ${error.message}`);
    }
  }

  // Marker en Snik som trukket.
  //
  // - `tidspunktTrukket` registreres LOKALT (Date.now()) og skrives inn i den
  //   abonnerte sniker-listen (this._lastKnownSniker) FØR noen nettverksskriving
  //   overhodet prøves. Dette sikrer at tidspunktet aldri går tapt, selv om
  //   selve Firestore-skrivingen under feiler (Requirement 5.5).
  // - Statusovergangen er idempotent: hvis Snik allerede har status 'trukket',
  //   er dette et no-op (ingen skriving, `trekkOpp` fra snikUtils returnerer
  //   Snik uendret) — i tråd med Requirement 5.4.
  // - Ved `unavailable`/nettverksfeil (eller hvis enheten allerede er offline)
  //   legges statusoppdateringen i offline-køen ('pending-sniker'), for
  //   synkronisering når tilkobling er gjenopprettet (samme mønster som
  //   `settSnik`, jf. design.md).
  // - Uansett om selve nettverksskrivingen eller kø-lagringen lykkes eller
  //   feiler, returneres den lokalt oppdaterte Snik — den lokale
  //   `tidspunktTrukket`-registreringen går aldri tapt (Requirement 5.5).
  async trekkOppSnik(snikId) {
    if (!firebaseService.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }
    if (!snikId) {
      throw new Error('Mangler snik-ID');
    }

    // Registreres lokalt FØRST, uavhengig av om nettverksskrivingen under
    // lykkes (Requirement 5.5).
    const tidspunktTrukket = Date.now();

    const lokalSnik = this._lastKnownSniker.find((snik) => snik.id === snikId);
    if (!lokalSnik) {
      throw new Error('Fant ikke snik');
    }

    // Ren, idempotent statusovergang (no-op hvis allerede 'trukket').
    const oppdatertSnik = trekkOpp(lokalSnik, tidspunktTrukket);

    // Oppdater den lokale kopien umiddelbart, slik at tidspunktet aldri går
    // tapt selv hvis nettverksskrivingen/kø-lagringen under feiler.
    this._lastKnownSniker = this._lastKnownSniker.map((snik) =>
      snik.id === snikId ? oppdatertSnik : snik
    );

    // Allerede trukket: no-op, ingen skriving nødvendig (Requirement 5.4).
    if (lokalSnik.status === 'trukket') {
      return oppdatertSnik;
    }

    const oppdatering = {
      status: oppdatertSnik.status,
      tidspunktTrukket: oppdatertSnik.tidspunktTrukket,
      totalTidUteMs: oppdatertSnik.totalTidUteMs
    };

    try {
      if (!navigator.onLine) {
        throw { code: 'unavailable' };
      }

      const snikRef = doc(this.snikerCollectionRef(), snikId);
      await setDoc(
        snikRef,
        {
          ...oppdatering,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
      return oppdatertSnik;
    } catch (error) {
      if (error.code === 'unavailable' || error.message?.includes('network') || !navigator.onLine) {
        try {
          await offlineQueue.addTo('pending-sniker', {
            type: 'trekkOpp',
            snikId,
            ...oppdatering
          });
        } catch (queueError) {
          // Selv om kø-lagringen feiler, er tidspunktTrukket allerede
          // registrert lokalt over — det går aldri tapt (Requirement 5.5).
          console.error('Kunne ikke legge snik-oppdatering i offline-kø:', queueError);
        }
        return oppdatertSnik;
      }
      // Uansett feiltype: statusendringen kan ha feilet, men tidspunktet
      // Snik ble trukket er allerede registrert lokalt og går ikke tapt
      // (Requirement 5.5).
      console.error('Kunne ikke oppdatere snik-status i Firestore:', error);
      return oppdatertSnik;
    }
  }

  // Delt skrivehjelper for felt-oppdateringer på en enkelt Snik (notat og
  // fangst-operasjoner). Følger nøyaktig samme online/offline-mønster som
  // settSnik/trekkOppSnik: prøv Firestore direkte (merge: true), og ved
  // `unavailable`/nettverksfeil (eller hvis enheten allerede er offline),
  // legg oppdateringen i offline-køen ('pending-sniker') for synkronisering
  // når tilkobling er gjenopprettet. Skriver ALDRI utenfor .../sniker, og
  // kaller ALDRI firebaseService.saveCatch/updateCatch (Requirement 14.2) —
  // dette er den eneste skrivemetoden brukt av oppdaterNotat/leggTilArt/
  // endreAntall/fjernArt under.
  async _skrivSnikOppdatering(snikId, oppdatering, type) {
    try {
      if (!navigator.onLine) {
        throw { code: 'unavailable' };
      }

      const snikRef = doc(this.snikerCollectionRef(), snikId);
      await setDoc(
        snikRef,
        {
          ...oppdatering,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch (error) {
      if (error.code === 'unavailable' || error.message?.includes('network') || !navigator.onLine) {
        try {
          await offlineQueue.addTo('pending-sniker', {
            type,
            snikId,
            ...oppdatering
          });
        } catch (queueError) {
          console.error(`Kunne ikke legge ${type}-oppdatering i offline-kø:`, queueError);
        }
        return;
      }
      console.error(`Kunne ikke skrive ${type} til Firestore:`, error);
    }
  }

  // Finn en Snik i den sist kjente, abonnerte listen, eller kast en tydelig
  // feil hvis den ikke finnes (samme validering som trekkOppSnik).
  _finnLokalSnik(snikId) {
    if (!firebaseService.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }
    if (!snikId) {
      throw new Error('Mangler snik-ID');
    }
    const lokalSnik = this._lastKnownSniker.find((snik) => snik.id === snikId);
    if (!lokalSnik) {
      throw new Error('Fant ikke snik');
    }
    return lokalSnik;
  }

  // Skriv en oppdatert Snik inn i den lokale, abonnerte listen, slik at
  // etterfølgende kall (f.eks. flere raske fangst-endringer) ser den
  // ferskeste tilstanden før neste Firestore-snapshot kommer inn.
  _settLokalSnik(oppdatertSnik) {
    this._lastKnownSniker = this._lastKnownSniker.map((snik) =>
      snik.id === oppdatertSnik.id ? oppdatertSnik : snik
    );
  }

  // Oppdater Snik_Notat for en Snik (Requirement 10.2, 10.3).
  //
  // Skriver KUN til .../sniker, aldri til .../catches, og kaller aldri
  // firebaseService.saveCatch/updateCatch (Requirement 14.2).
  async oppdaterNotat(snikId, notat) {
    const lokalSnik = this._finnLokalSnik(snikId);
    const rentNotat = typeof notat === 'string' ? notat : '';
    const oppdatertSnik = { ...lokalSnik, notat: rentNotat };
    this._settLokalSnik(oppdatertSnik);

    await this._skrivSnikOppdatering(snikId, { notat: rentNotat }, 'oppdaterNotat');
    return oppdatertSnik;
  }

  // Legg til en fiskeart i Snik_Fangst for en Snik (Requirement 8.4, 9.6).
  //
  // Bruker den rene funksjonen leggTilArt fra snikUtils.js for å beregne den
  // nye fangstlisten, og skriver deretter KUN til .../sniker. Kaller aldri
  // firebaseService.saveCatch/updateCatch og oppretter aldri et dokument i
  // .../catches (Requirement 14.2).
  async leggTilArt(snikId, art) {
    const lokalSnik = this._finnLokalSnik(snikId);
    const nyFangst = leggTilArtUtil(lokalSnik.fangst, art);
    const oppdatertSnik = { ...lokalSnik, fangst: nyFangst };
    this._settLokalSnik(oppdatertSnik);

    await this._skrivSnikOppdatering(snikId, { fangst: nyFangst }, 'leggTilArt');
    return oppdatertSnik;
  }

  // Endre antallet på en artslinje i Snik_Fangst for en Snik med et gitt
  // delta (Requirement 9.2, 9.3).
  //
  // Bruker den rene funksjonen endreAntall fra snikUtils.js, og skriver
  // deretter KUN til .../sniker (Requirement 14.2).
  async endreAntall(snikId, art, delta) {
    const lokalSnik = this._finnLokalSnik(snikId);
    const nyFangst = endreAntallUtil(lokalSnik.fangst, art, delta);
    const oppdatertSnik = { ...lokalSnik, fangst: nyFangst };
    this._settLokalSnik(oppdatertSnik);

    await this._skrivSnikOppdatering(snikId, { fangst: nyFangst }, 'endreAntall');
    return oppdatertSnik;
  }

  // Fjern artslinjen for en gitt art fra Snik_Fangst for en Snik, uansett
  // gjeldende antall (Requirement 9.5).
  //
  // Bruker den rene funksjonen fjernArt fra snikUtils.js, og skriver deretter
  // KUN til .../sniker (Requirement 14.2).
  async fjernArt(snikId, art) {
    const lokalSnik = this._finnLokalSnik(snikId);
    const nyFangst = fjernArtUtil(lokalSnik.fangst, art);
    const oppdatertSnik = { ...lokalSnik, fangst: nyFangst };
    this._settLokalSnik(oppdatertSnik);

    await this._skrivSnikOppdatering(snikId, { fangst: nyFangst }, 'fjernArt');
    return oppdatertSnik;
  }

  // Slett en Snik permanent (både aktive og trukne sniker kan slettes).
  //
  // Fjerner den lokale kopien umiddelbart (optimistisk), slik at UI oppdateres
  // uten å vente på Firestore, og ruller tilbake hvis selve slettingen feiler.
  async slettSnik(snikId) {
    if (!firebaseService.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }
    if (!snikId) {
      throw new Error('Mangler snik-ID');
    }

    const forrigeListe = this._lastKnownSniker;
    this._lastKnownSniker = forrigeListe.filter((snik) => snik.id !== snikId);

    try {
      const snikRef = doc(this.snikerCollectionRef(), snikId);
      await deleteDoc(snikRef);
    } catch (error) {
      // Rull tilbake den lokale, optimistiske fjerningen hvis slettingen feiler,
      // slik at UI ikke viser en Snik som "slettet" som faktisk fortsatt finnes.
      this._lastKnownSniker = forrigeListe;
      console.error('Kunne ikke slette snik:', error);
      throw new Error(`Kunne ikke slette snik: ${error.message}`);
    }
  }

  // Synkroniser 'pending-sniker'-køen mot Firestore.
  //
  // Følger nøyaktig samme mønster som firebaseService.syncOfflineQueue():
  // - Hindrer parallelle synkroniseringer (race condition) via this._syncingSniker.
  // - Krever innlogget bruker og at enheten er online.
  // - For hvert element: fjern fra køen FØRST (unngår duplikater ved retry),
  //   deretter skriv til Firestore. Ved feil legges elementet tilbake i køen
  //   og synkroniseringen stopper (prøves igjen senere).
  //
  // Et kø-element representerer én av to ting, avhengig av om det har et
  // `type`-felt (se _skrivSnikOppdatering/trekkOppSnik/settSnik over):
  // - Uten `type`: en `settSnik`-oppføring — full Snik-data (inkl. userId)
  //   for en Snik som ennå ikke finnes i Firestore. Synkroniseres med et nytt
  //   dokument under .../sniker (samme opprettelseslogikk som settSnik).
  // - Med `type` ('trekkOpp' | 'oppdaterNotat' | 'leggTilArt' | 'endreAntall'
  //   | 'fjernArt') og `snikId`: en feltoppdatering på en EKSISTERENDE Snik.
  //   Synkroniseres som en merge-oppdatering av samme dokument (samme mønster
  //   som _skrivSnikOppdatering).
  //
  // Kalt fra samme `online`-event-lytter i App.js som allerede synkroniserer
  // fangster, og ved appstart (Requirement 3.10).
  async syncOfflineSniker() {
    if (this._syncingSniker) return 0;
    if (!firebaseService.auth.currentUser || !navigator.onLine) return 0;

    this._syncingSniker = true;
    try {
      const pending = await offlineQueue.getAllFrom('pending-sniker');
      let synced = 0;

      for (const item of pending) {
        try {
          const { id, queuedAt, type, snikId, ...rest } = item;

          // Fjern fra køen FØRST for å unngå duplikater ved retry.
          await offlineQueue.removeFrom('pending-sniker', id);

          if (!type) {
            // settSnik-oppføring: opprett en ny Snik i Firestore.
            const docRef = doc(this.snikerCollectionRef());
            await setDoc(docRef, {
              ...rest,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          } else {
            // Feltoppdatering (trekkOpp/oppdaterNotat/leggTilArt/endreAntall/
            // fjernArt) på en eksisterende Snik.
            const snikRef = doc(this.snikerCollectionRef(), snikId);
            await setDoc(
              snikRef,
              {
                ...rest,
                updatedAt: serverTimestamp()
              },
              { merge: true }
            );
          }

          synced++;
        } catch (err) {
          console.error('Snik-sync feilet for kø-element:', item.id, err);
          // Legg tilbake i køen hvis Firestore-skrivingen feilet.
          try {
            const { id, ...restItem } = item;
            await offlineQueue.addTo('pending-sniker', restItem);
          } catch (requeueErr) {
            console.error('Kunne ikke legge snik-kø-element tilbake i kø:', requeueErr);
          }
          break; // Stopp ved feil, prøv igjen senere
        }
      }
      return synced;
    } finally {
      this._syncingSniker = false;
    }
  }

  // Normaliser ett Firestore-dokument til et gyldig Snik-objekt (jf. Data Models
  // i design.md). Manglende/ugyldige felt fylles med sensible defaults i stedet
  // for å la ett korrupt dokument kaste feil og stoppe hele abonnementet.
  normalizeSnikDoc(doc) {
    try {
      const data = doc.data() || {};
      return {
        id: doc.id,
        lopenummer: Number.isFinite(data.lopenummer) ? data.lopenummer : 0,
        posisjon: this.normalizePosisjon(data.posisjon),
        tidspunktSatt: Number.isFinite(data.tidspunktSatt) ? data.tidspunktSatt : Date.now(),
        status: data.status === 'trukket' ? 'trukket' : 'aktiv',
        tidspunktTrukket: Number.isFinite(data.tidspunktTrukket) ? data.tidspunktTrukket : null,
        totalTidUteMs: Number.isFinite(data.totalTidUteMs) ? data.totalTidUteMs : null,
        notat: typeof data.notat === 'string' ? data.notat : '',
        fangst: this.normalizeFangst(data.fangst),
        userId: typeof data.userId === 'string' ? data.userId : (firebaseService.auth.currentUser?.uid || ''),
        createdAt: data.createdAt ?? null,
        updatedAt: data.updatedAt ?? null
      };
    } catch (err) {
      console.error('Korrupt snik-dokument hoppet over:', doc.id, err);
      return null;
    }
  }

  // Posisjon må ha numerisk lat/lng, ellers faller vi tilbake til (0, 0) i
  // stedet for å la et manglende/korrupt posisjonsfelt kaste feil videre.
  normalizePosisjon(posisjon) {
    if (
      posisjon &&
      typeof posisjon === 'object' &&
      Number.isFinite(posisjon.lat) &&
      Number.isFinite(posisjon.lng)
    ) {
      return { lat: posisjon.lat, lng: posisjon.lng };
    }
    return { lat: 0, lng: 0 };
  }

  // Snik_Fangst må alltid være en liste av { art, latin, antall }. Ugyldige
  // linjer (ikke objekt) filtreres ut, og ugyldige felt innad i en linje
  // fylles med sensible defaults.
  normalizeFangst(fangst) {
    if (!Array.isArray(fangst)) return [];
    return fangst
      .filter((linje) => linje && typeof linje === 'object')
      .map((linje) => ({
        art: typeof linje.art === 'string' ? linje.art : '',
        latin: typeof linje.latin === 'string' ? linje.latin : '',
        antall: Number.isFinite(linje.antall) && linje.antall >= 1 ? linje.antall : 1
      }));
  }
}

export default new SnikService();
